// pages/Login.jsx (Admin Portal)
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

// ============ DESIGN TOKENS ============
const tokens = {
  colors: {
    primary: {
      50: '#fdf4ff',
      100: '#fae8ff',
      200: '#f5d0fe',
      300: '#f0abfc',
      400: '#e879f9',
      500: '#d946ef',
      600: '#c026d3',
      700: '#a21caf',
      800: '#86198f',
      900: '#701a75',
    },
    neutral: {
      0: '#ffffff',
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617',
    },
    success: { light: '#ecfdf5', main: '#10b981', dark: '#059669' },
    warning: { light: '#fffbeb', main: '#f59e0b', dark: '#d97706' },
    error: { light: '#fef2f2', main: '#ef4444', dark: '#dc2626' },
    accent: {
      purple: '#8b5cf6',
      pink: '#ec4899',
      indigo: '#6366f1',
    },
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
    '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    glow: '0 0 40px rgba(139, 92, 246, 0.2)',
    glowStrong: '0 0 60px rgba(139, 92, 246, 0.3)',
  },
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  radius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    '3xl': '32px',
    full: '9999px',
  },
};

// ============ ANIMATIONS ============
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const fadeInLeft = keyframes`
  from { 
    opacity: 0; 
    transform: translateX(-30px); 
  }
  to { 
    opacity: 1; 
    transform: translateX(0); 
  }
`;

const fadeInRight = keyframes`
  from { 
    opacity: 0; 
    transform: translateX(30px); 
  }
  to { 
    opacity: 1; 
    transform: translateX(0); 
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const floatReverse = keyframes`
  0%, 100% { transform: translateY(-20px); }
  50% { transform: translateY(0px); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const scaleIn = keyframes`
  from { 
    opacity: 0; 
    transform: scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: scale(1); 
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// ============ ICON COMPONENTS ============
const Icons = {
  Mail: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Lock: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Eye: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  ArrowRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  AlertCircle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Loader: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  ),
  Shield: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Users: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Settings: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

// ============ STYLED COMPONENTS ============
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: ${tokens.colors.neutral[50]};
  position: relative;
  overflow: hidden;
`;

// Animated background elements
const BackgroundOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  pointer-events: none;
  
  &.orb-1 {
    width: 500px;
    height: 500px;
    background: linear-gradient(135deg, ${tokens.colors.accent.purple}40, ${tokens.colors.accent.pink}40);
    top: -150px;
    right: -100px;
    animation: ${float} 20s ease-in-out infinite;
  }
  
  &.orb-2 {
    width: 400px;
    height: 400px;
    background: linear-gradient(135deg, ${tokens.colors.accent.indigo}30, ${tokens.colors.accent.purple}30);
    bottom: -100px;
    left: -100px;
    animation: ${floatReverse} 15s ease-in-out infinite;
  }
  
  &.orb-3 {
    width: 300px;
    height: 300px;
    background: linear-gradient(135deg, ${tokens.colors.accent.pink}25, ${tokens.colors.accent.purple}25);
    top: 50%;
    left: 30%;
    animation: ${float} 25s ease-in-out infinite;
  }
`;

const GridPattern = styled.div`
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(${tokens.colors.neutral[200]}40 1px, transparent 1px),
    linear-gradient(90deg, ${tokens.colors.neutral[200]}40 1px, transparent 1px);
  background-size: 60px 60px;
  opacity: 0.4;
  pointer-events: none;
`;

// Left Section - Branding
const BrandSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 60px;
  position: relative;
  background: linear-gradient(135deg, ${tokens.colors.neutral[900]} 0%, ${tokens.colors.neutral[950]} 100%);
  overflow: hidden;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const BrandPattern = styled.div`
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 25% 25%, ${tokens.colors.accent.purple}15 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, ${tokens.colors.accent.pink}10 0%, transparent 50%);
  pointer-events: none;
`;

const BrandDots = styled.div`
  position: absolute;
  inset: 0;
  background-image: radial-gradient(${tokens.colors.neutral[700]} 1px, transparent 1px);
  background-size: 24px 24px;
  opacity: 0.4;
  pointer-events: none;
`;

const BrandContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 480px;
  animation: ${fadeInLeft} 0.8s ease-out;
`;

const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 48px;
`;

const LogoMark = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, ${tokens.colors.accent.purple} 0%, ${tokens.colors.accent.pink} 100%);
  border-radius: ${tokens.radius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(139, 92, 246, 0.35);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${tokens.radius.xl};
  }
`;

const LogoText = styled.div`
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${tokens.colors.neutral[0]};
    margin: 0;
    letter-spacing: -0.5px;
  }

  span {
    font-size: 0.8rem;
    color: ${tokens.colors.accent.purple};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const BrandHeadline = styled.h2`
  font-size: 2.75rem;
  font-weight: 800;
  color: ${tokens.colors.neutral[0]};
  line-height: 1.15;
  margin: 0 0 24px 0;
  letter-spacing: -1px;

  .highlight {
    background: linear-gradient(135deg, ${tokens.colors.accent.purple} 0%, ${tokens.colors.accent.pink} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const BrandDescription = styled.p`
  font-size: 1.1rem;
  color: ${tokens.colors.neutral[400]};
  line-height: 1.7;
  margin: 0 0 48px 0;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  animation: ${fadeInUp} 0.6s ease-out ${props => props.$delay} backwards;
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: ${tokens.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    color: ${tokens.colors.accent.purple};
  }
`;

const FeatureText = styled.div`
  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    color: ${tokens.colors.neutral[100]};
    margin: 0 0 4px 0;
  }

  p {
    font-size: 0.85rem;
    color: ${tokens.colors.neutral[500]};
    margin: 0;
    line-height: 1.5;
  }
`;

// Right Section - Form
const FormSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  z-index: 10;

  @media (max-width: 1024px) {
    min-height: 100vh;
  }

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 420px;
  animation: ${fadeInRight} 0.8s ease-out;

  @media (max-width: 1024px) {
    animation: ${fadeInUp} 0.8s ease-out;
  }
`;

const MobileLogo = styled.div`
  display: none;
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileLogoMark = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, ${tokens.colors.accent.purple} 0%, ${tokens.colors.accent.pink} 100%);
  border-radius: ${tokens.radius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 8px 24px rgba(139, 92, 246, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${tokens.radius.xl};
  }
`;

const MobileLogoText = styled.div`
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${tokens.colors.neutral[900]};
    margin: 0 0 4px 0;
  }

  span {
    font-size: 0.85rem;
    color: ${tokens.colors.accent.purple};
    font-weight: 600;
  }
`;

const FormCard = styled.div`
  background: ${tokens.colors.neutral[0]};
  border-radius: ${tokens.radius['2xl']};
  padding: 40px;
  box-shadow: ${tokens.shadows.xl}, ${tokens.shadows.glow};
  border: 1px solid ${tokens.colors.neutral[200]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${tokens.colors.accent.purple}, ${tokens.colors.accent.pink});
  }

  @media (max-width: 480px) {
    padding: 32px 24px;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 32px;
  text-align: center;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${tokens.colors.neutral[900]};
    margin: 0 0 8px 0;
  }

  p {
    font-size: 0.95rem;
    color: ${tokens.colors.neutral[500]};
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${fadeInUp} 0.5s ease-out ${props => props.$delay || '0s'} backwards;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${tokens.colors.neutral[700]};
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:focus-within .input-icon {
    color: ${tokens.colors.accent.purple};
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${tokens.colors.neutral[400]};
  transition: color ${tokens.transitions.fast};
  pointer-events: none;
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 ${props => props.$hasToggle ? '48px' : '16px'} 0 48px;
  background: ${tokens.colors.neutral[50]};
  border: 2px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.radius.lg};
  font-size: 0.95rem;
  color: ${tokens.colors.neutral[900]};
  transition: all ${tokens.transitions.fast};
  outline: none;

  &::placeholder {
    color: ${tokens.colors.neutral[400]};
  }

  &:hover:not(:focus):not(:disabled) {
    border-color: ${tokens.colors.neutral[300]};
    background: ${tokens.colors.neutral[0]};
  }

  &:focus {
    border-color: ${tokens.colors.accent.purple};
    background: ${tokens.colors.neutral[0]};
    box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px ${tokens.colors.neutral[50]} inset;
    -webkit-text-fill-color: ${tokens.colors.neutral[900]};
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: ${tokens.radius.md};
  color: ${tokens.colors.neutral[400]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};

  &:hover:not(:disabled) {
    background: ${tokens.colors.neutral[100]};
    color: ${tokens.colors.neutral[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  height: 52px;
  background: linear-gradient(135deg, ${tokens.colors.accent.purple} 0%, ${tokens.colors.accent.pink} 100%);
  color: white;
  border: none;
  border-radius: ${tokens.radius.lg};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  overflow: hidden;
  transition: all ${tokens.transitions.fast};
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
  animation: ${fadeInUp} 0.5s ease-out 0.3s backwards;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, ${tokens.colors.accent.pink} 0%, ${tokens.colors.primary[600]} 100%);
    opacity: 0;
    transition: opacity ${tokens.transitions.fast};
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(139, 92, 246, 0.4);

    &::before {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  span, svg {
    position: relative;
    z-index: 1;
  }

  .loader {
    animation: ${spin} 0.8s linear infinite;
  }
`;

// Alert Component
const Alert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: ${tokens.radius.lg};
  margin-bottom: 24px;
  animation: ${scaleIn} 0.3s ease-out;
  
  &.error {
    background: ${tokens.colors.error.light};
    border: 1px solid rgba(239, 68, 68, 0.2);
    color: ${tokens.colors.error.dark};
  }
  
  &.success {
    background: ${tokens.colors.success.light};
    border: 1px solid rgba(16, 185, 129, 0.2);
    color: ${tokens.colors.success.dark};
  }

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .alert-content {
    flex: 1;

    .title {
      font-weight: 600;
      font-size: 0.9rem;
      margin-bottom: 2px;
    }

    .message {
      font-size: 0.85rem;
      opacity: 0.9;
    }
  }
`;

const SecurityNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${tokens.colors.neutral[200]};
  animation: ${fadeInUp} 0.5s ease-out 0.4s backwards;

  svg {
    width: 16px;
    height: 16px;
    color: ${tokens.colors.success.main};
  }

  span {
    font-size: 0.8rem;
    color: ${tokens.colors.neutral[500]};
  }
`;

// ============ COMPONENT ============
const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const emailRef = useRef(null);

  // Focus email input on mount
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);

    try {
      await login(email, password);
      setStatus({ type: 'success', title: 'Welcome back!', message: 'Redirecting to dashboard...' });
      toast.success('Welcome back, Admin!');
      setTimeout(() => {
        navigate('/dashboard');
      }, 800);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Invalid credentials. Please try again.';
      setStatus({ type: 'error', title: 'Login failed', message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      {/* Animated Background */}
      <BackgroundOrb className="orb-1" />
      <BackgroundOrb className="orb-2" />
      <BackgroundOrb className="orb-3" />
      <GridPattern />

      {/* Left Brand Section */}
      <BrandSection>
        <BrandPattern />
        <BrandDots />
        
        <BrandContent>
          <BrandLogo>
            <LogoMark>
              <img src="/logo192.png" alt="Visioning Assessment" />
            </LogoMark>
            <LogoText>
              <h1>Visioning Assessment</h1>
              <span>Admin Portal</span>
            </LogoText>
          </BrandLogo>

          <BrandHeadline>
            Platform <span className="highlight">Administration</span> Made Simple
          </BrandHeadline>

          <BrandDescription>
            Manage practitioners, monitor assessments, and oversee platform 
            operations from a single powerful dashboard.
          </BrandDescription>

          <FeatureList>
            <FeatureItem $delay="0.3s">
              <FeatureIcon>
                <Icons.Users />
              </FeatureIcon>
              <FeatureText>
                <h4>Practitioner Management</h4>
                <p>Approve, manage, and monitor all practitioners on the platform</p>
              </FeatureText>
            </FeatureItem>

            <FeatureItem $delay="0.4s">
              <FeatureIcon>
                <Icons.Shield />
              </FeatureIcon>
              <FeatureText>
                <h4>Secure Access Control</h4>
                <p>Role-based permissions with enterprise-grade security</p>
              </FeatureText>
            </FeatureItem>

            <FeatureItem $delay="0.5s">
              <FeatureIcon>
                <Icons.Settings />
              </FeatureIcon>
              <FeatureText>
                <h4>Complete Oversight</h4>
                <p>Full visibility into all platform activities and metrics</p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
        </BrandContent>
      </BrandSection>

      {/* Right Form Section */}
      <FormSection>
        <FormContainer>
          {/* Mobile Logo */}
          <MobileLogo>
            <MobileLogoMark>
              <img src="/logo192.png" alt="Visioning Assessment" />
            </MobileLogoMark>
            <MobileLogoText>
              <h1>Visioning Assessment</h1>
              <span>Admin Portal</span>
            </MobileLogoText>
          </MobileLogo>

          <FormCard>
            <FormHeader>
              <h2>Admin Sign In</h2>
              <p>Enter your credentials to access the admin panel</p>
            </FormHeader>

            {status && (
              <Alert className={status.type}>
                {status.type === 'error' ? <Icons.AlertCircle /> : <Icons.Check />}
                <div className="alert-content">
                  <div className="title">{status.title}</div>
                  <div className="message">{status.message}</div>
                </div>
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <FormGroup $delay="0.1s">
                <Label htmlFor="email">Email Address</Label>
                <InputWrapper>
                  <Input
                    ref={emailRef}
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                  <InputIcon className="input-icon">
                    <Icons.Mail />
                  </InputIcon>
                </InputWrapper>
              </FormGroup>

              <FormGroup $delay="0.2s">
                <Label htmlFor="password">Password</Label>
                <InputWrapper>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                    $hasToggle
                  />
                  <InputIcon className="input-icon">
                    <Icons.Lock />
                  </InputIcon>
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                  </PasswordToggle>
                </InputWrapper>
              </FormGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Icons.Loader className="loader" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <Icons.ArrowRight />
                  </>
                )}
              </SubmitButton>
            </Form>

            <SecurityNote>
              <Icons.Shield />
              <span>Protected by enterprise-grade security</span>
            </SecurityNote>
          </FormCard>
        </FormContainer>
      </FormSection>
    </PageContainer>
  );
};

export default Login;